import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, MapPin, CalendarDays, Award } from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";

const IMG = "https://static.prod-images.emergentagent.com/jobs/152026f0-1513-45ae-b838-38d02f56f416/images";
const ATELIER = `${IMG}/ebcdf2e0e87505f5d26eb1d7cd400ced0705a69f15b22d05a6d88245f869bc4b.jpeg`;
const FITTING = `${IMG}/169f764d30d9e7b932aee909c3104469eb5bb7b22e683710859eb2f1b0fb88fd.jpeg`;
const HERO = `${IMG}/d40983d2190c81a4aca92d3e3a57ceb922f29ce3aa8755c84a03f08a4676afd1.jpeg`;

export default function Presse() {
  const { t } = useTranslation();

  const SALONS = [
    {
      id: "milan",
      city: t("presse.milanCity"),
      event: "EXPORIVASCHUH GADABAGS",
      edition: t("presse.milanEdition"),
      icon: Award,
      paragraphs: [t("presse.milanP1"), t("presse.milanP2")],
      quotes: [t("presse.milanQ1"), t("presse.milanQ2")],
    },
    {
      id: "newyork",
      city: t("presse.nyCity"),
      event: "NY NOW",
      edition: t("presse.nyEdition"),
      icon: MapPin,
      paragraphs: [t("presse.nyP1"), t("presse.nyP2")],
      quotes: [t("presse.nyQ1")],
    },
  ];

  return (
    <main data-testid="presse-page">
      <section className="relative flex min-h-[60vh] items-end overflow-hidden">
        <img src={HERO} alt="B.KOSS — Campagne" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-noir via-noir/60 to-noir/30" />
        <div className="relative mx-auto w-full max-w-[1500px] px-6 pb-20 pt-44 lg:px-12">
          <Reveal>
            <p className="eyebrow">{t("presse.eyebrow")}</p>
            <h1 className="mt-5 font-serif text-4xl uppercase leading-tight tracking-tight text-ivoire sm:text-5xl lg:text-6xl">
              {t("presse.pre")} <span className="italic normal-case text-or">{t("presse.it")}</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1100px] px-6 py-20 lg:px-12 lg:py-28">
        <Reveal>
          <p className="text-lg leading-relaxed text-argile">
            {t("presse.intro")}
          </p>
        </Reveal>

        <div className="mt-16 space-y-20">
          {SALONS.map((s, idx) => (
            <Reveal key={s.id} delay={0.05}>
              <article data-testid={`presse-salon-${s.id}`} className="gold-frame border border-ligne bg-card2">
                <div className={`grid lg:grid-cols-2 ${idx % 2 === 1 ? "lg:[direction:rtl]" : ""}`}>
                  <div className="[direction:ltr]">
                    <img
                      src={idx === 0 ? ATELIER : FITTING}
                      alt={`${s.event} — B.KOSS`}
                      className="h-full max-h-[420px] w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-8 lg:p-12 [direction:inherit]">
                    <div className="flex items-center gap-3">
                      <s.icon size={17} className="text-or" strokeWidth={1.5} />
                      <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-or">{s.edition}</p>
                    </div>
                    <h2 className="mt-4 font-serif text-3xl uppercase tracking-tight text-ivoire">{s.event}</h2>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-argile">
                      <CalendarDays size={11} className="me-2 inline text-or" />
                      {s.city}
                    </p>
                    {s.paragraphs.map((p, i) => (
                      <p key={i} className="mt-5 leading-relaxed text-argile">{p}</p>
                    ))}
                    {s.quotes.map((q, i) => (
                      <blockquote key={i} className="mt-6 border-s-2 border-or ps-5 font-serif text-lg italic leading-relaxed text-ivoire">
                        « {q} »
                        <footer className="mt-2 font-mono text-[9px] uppercase tracking-[0.25em] text-argile">{t("presse.quoteBy")}</footer>
                      </blockquote>
                    ))}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-20 flex flex-col items-center gap-6 border-t border-ligne pt-16 text-center">
            <MaskedLine className="font-serif text-2xl uppercase tracking-tight text-ivoire sm:text-3xl">
              {t("presse.nextPre")} <span className="italic normal-case text-or">{t("presse.nextIt")}</span>
            </MaskedLine>
            <Link
              data-testid="presse-cta-boutique"
              to="/boutique"
              className="group flex items-center gap-3 bg-or px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all hover:bg-orclair"
            >
              {t("presse.cta")} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1 rtl:rotate-180" />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
